import * as AppVersionType from "fleet/constant/app-version-type";
import PropTypes from "prop-types";
import React from "react";
import {
  Row,
  Col,
  Button,
  FormGroup,
  FormText,
  FormLabel,
} from "react-bootstrap";
import { Link, Redirect } from "react-router-dom";
import Select from "react-select";
import { getScreenshots, icon, name } from "redux-store/applications/selectors";
import Carousel from "ui/carousel";
import DialogModal from "ui/dialog-modal";
import { Header } from "ui/header";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { sortByDisplayNameAsc } from "util/sort-by-prop";
import { utcToLocal } from "util/time";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class AppDetailsPage extends React.PureComponent {
  static get propTypes() {
    return {
      app: PropTypes.object,
      editApp: PropTypes.func.isRequired,
      isAdmin: PropTypes.bool.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      type: PropTypes.oneOf([AppVersionType.STABLE, AppVersionType.TESTING])
        .isRequired,
    };
  }

  constructor() {
    super();

    this.state = {
      errorMessage: "",
      showMakeStableModal: false,
      showMakePrivateModal: false,
      showMakePublicModal: false,
      showSwitchVersionModal: false,
      selectedVersion: null,
    };
  }

  _edit(operation, status, versionId = null) {
    const { app, editApp } = this.props;
    const serviceArgs = {
      status,
      versionId,
      operation,
      id: app.id,
    };
    return editApp(serviceArgs).catch((e) => {
      this.setState({
        errorMessage: `Failed to update the application. \
            Please try again later.`,
      });
    });
  }

  _badges() {
    const { app, type } = this.props;
    let badges = [];
    if (app.type === "APPLICATION") {
      badges.push(
        <span key="type" className="badge bg-yellow">
          Application
        </span>,
      );
    }

    if (app.private) {
      badges.push(
        <span key="private" className="badge bg-gray">
          Private
        </span>,
      );
    } else {
      badges.push(
        <span key="private" className="badge bg-green">
          Public
        </span>,
      );
    }

    if (app.required) {
      badges.push(
        <span key="required" className="badge bg-red">
          Required
        </span>,
      );
    }

    if (app.is_installed || app.is_own) {
      badges.push(
        <span key="install" className="badge bg-blue">
          Installed
        </span>,
      );
    } else {
      badges.push(
        <span key="install" className="badge bg-gray">
          Not Installed
        </span>,
      );
    }

    if (type === AppVersionType.STABLE) {
      badges.push(
        <span key="version" className="badge bg-teal">
          Stable
        </span>,
      );
    } else {
      badges.push(
        <span key="version" className="badge bg-gray">
          Testing
        </span>,
      );
    }

    return badges;
  }

  _desc() {
    const { app, type } = this.props;
    const version =
      type === AppVersionType.TESTING
        ? app.testing_version.version
        : app.stable_version.version;
    return `${name(app)} @${version}`;
  }

  _title() {
    const { app, type } = this.props;
    const appName = name(app);
    if (type === AppVersionType.TESTING) {
      return `[TESTING] ${appName}`;
    }
    return appName;
  }

  _versionOptions() {
    const { app } = this.props;

    const currentStableVersion = app.stable_version
      ? app.stable_version.id
      : null;
    const currentTestingVersion = app.testing_version
      ? app.testing_version.id
      : null;

    const appVersions = app.versions || [];

    const versionOptions = appVersions
      .filter((version) => {
        return (
          version.id !== currentStableVersion &&
          version.id !== currentTestingVersion
        );
      })
      .map((version) => {
        const uploadDate = version.upload_date;
        return {
          value: version.id,
          label: version.version + " - " + utcToLocal(uploadDate),
        };
      });

    return versionOptions;
  }

  _onVersionSelect(value) {
    this.setState({ selectedVersion: value });
  }

  _renderVersionSwitchButton() {
    const { app, type } = this.props;
    const stable = app.stable_version;
    const testing = app.testing_version;

    let link = null;

    if (stable && testing) {
      if (type === AppVersionType.STABLE) {
        link = (
          <Link
            className="btn btn-default"
            to={`/applications/${app.id}/testing`}
          >
            View Testing Version
          </Link>
        );
      } else {
        link = (
          <Link className="btn btn-default" to={`/applications/${app.id}`}>
            View Stable Version
          </Link>
        );
      }
    }

    return <div className="page-tools">{link}</div>;
  }

  _renderParameters() {
    const { app, type } = this.props;
    const userParams =
      type === AppVersionType.TESTING
        ? app.testing_version.user_params
        : app.stable_version.user_params;

    if (!userParams) {
      return;
    }

    return (
      <Box title="Configuration Parameters">
        {this._renderRequiredParams(userParams)}
        {this._renderOptionalParams(userParams)}
      </Box>
    );
  }

  _renderRequiredParams(params) {
    const requiredParams = params
      .filter((s) => s.required)
      .sort(sortByDisplayNameAsc);

    const requiredItems = requiredParams.map((param) => {
      const { description, display_name: displayName, name } = param;

      return (
        <div className="mb-4" data-qa={name} key={name}>
          <dt>{displayName}</dt>
          <dd>{description}</dd>
        </div>
      );
    });

    return (
      <div data-qa="required" className="mb-25">
        <Header title="Required parameters" />
        {requiredParams.length === 0 ? (
          <span>
            This application doesn&#39;t have any required parameters.
          </span>
        ) : (
          <dl>{requiredItems}</dl>
        )}
      </div>
    );
  }

  _renderOptionalParams(params) {
    const optionalParams = params
      .filter((s) => !s.required)
      .sort(sortByDisplayNameAsc);

    const optionalItems = optionalParams.map((param) => {
      const {
        default_value,
        description,
        name,
        display_name: displayName,
      } = param;
      const defaultValue = JSON.stringify(default_value);

      return (
        <div className="mb-4" data-qa={name} key={name}>
          <dt>{displayName}</dt>
          <dd>{description}</dd>
          <dd>
            Default Value: <i>{defaultValue}</i>
          </dd>
        </div>
      );
    });

    return (
      <div data-qa="optional">
        <Header title="Optional parameters" />
        {optionalParams.length === 0 ? (
          <span>
            This application doesn&#39;t have any optional parameters.
          </span>
        ) : (
          <dl>{optionalItems}</dl>
        )}
      </div>
    );
  }

  _renderScreenshots() {
    const { app, type } = this.props;
    const screenshots = getScreenshots(app, type);
    if (screenshots.length > 0) {
      return (
        <Box title="Screenshots">
          <Row>
            <Col xs={12}>
              <Carousel items={screenshots} />
            </Col>
          </Row>
        </Box>
      );
    }

    return null;
  }

  _renderControls() {
    const { isAdmin } = this.props;
    if (!isAdmin) {
      return null;
    }

    return (
      <div className="app-details-controls">
        {this._renderApplicationStatusButton()}
        <br />
        <br />
        <Notification error={this.state.errorMessage} />
        {this._renderMakeStableModal()}
        {this._renderMakePrivateModal()}
        {this._renderMakePublicModal()}
        {this._renderSwitchVersionModal()}
      </div>
    );
  }

  _renderApplicationStatusButton() {
    const { app, type } = this.props;
    const stableApp = app.stable_version;
    const testingApp = app.testing_version;

    const stableV = stableApp ? stableApp.version : null;
    const testingV = testingApp ? testingApp.version : null;

    if (stableV === testingV && type === AppVersionType.TESTING) {
      const versionChangeHandler = () => {
        this.setState({ showSwitchVersionModal: true });
      };
      rollbackBtn = (
        <Button size="sm" variant="warning" onClick={versionChangeHandler}>
          Change Testing Version
        </Button>
      );

      return <div>{rollbackBtn}</div>;
    }

    const {
      is_installed: isInstalled,
      is_own: owned,
      private: isPrivate,
      required: isRequired,
    } = app;

    let style = "";
    let text = "";
    let onClick = null;

    let rollbackBtn = null;

    if (!owned && isRequired) {
      return null;
    }

    if (owned) {
      if (type === AppVersionType.TESTING) {
        style = "primary";
        text = "Make Stable";
        onClick = () => {
          this.setState({ showMakeStableModal: true });
        };
      } else if (isPrivate) {
        style = "primary";
        text = "Make Public";
        onClick = () => {
          this._edit("public", true);
          this.setState({ showMakePublicModal: true });
        };
      } else if (!isRequired) {
        style = "default";
        text = "Make Private";
        onClick = () => {
          this.setState({ showMakePrivateModal: true });
        };
      }

      if (
        (type === AppVersionType.TESTING || type === AppVersionType.STABLE) &&
        this._versionOptions().length > 0
      ) {
        const onRollbackClick = () => {
          this.setState({ showSwitchVersionModal: true });
        };
        const versionType =
          type === AppVersionType.TESTING ? "Testing" : "Stable";
        rollbackBtn = (
          <Button size="sm" variant="warning" onClick={onRollbackClick}>
            {`Change ${versionType} Version`}
          </Button>
        );
      }
    } else if (isInstalled) {
      style = "default";
      text = "Uninstall";
      onClick = () => {
        this._edit("install", false);
      };
    } else {
      style = "primary";
      text = "Install";
      onClick = () => {
        this._edit("install", true);
      };
    }

    const hasUpdateBtn = onClick !== null;
    return (
      <div>
        {hasUpdateBtn && (
          <Button size="sm" variant={style} onClick={onClick}>
            {text}
          </Button>
        )}
        {rollbackBtn}
      </div>
    );
  }

  _renderAppVersions() {
    return (
      <FormGroup>
        <FormLabel>Select Version</FormLabel>
        <Select
          name="switch-app-version"
          onChange={this._onVersionSelect.bind(this)}
          options={this._versionOptions()}
          placeholder="Select version"
          value={this.state.selectedVersion}
        />
        <FormText>
          Please select the application version you want to switch to.
        </FormText>
      </FormGroup>
    );
  }

  _renderSwitchVersionModal() {
    const { type } = this.props;
    let targetSwitch = null;

    if (!this.state.showSwitchVersionModal) {
      return null;
    }

    const _hide = () => {
      this.setState({ showSwitchVersionModal: false });
    };

    const _switchStableVersion = () => {
      this._edit(
        "switch_stable_version",
        true,
        this.state.selectedVersion.value,
      );
      this.setState({ showSwitchVersionModal: false });
    };

    const _switchTestingVersion = () => {
      this._edit(
        "switch_testing_version",
        true,
        this.state.selectedVersion.value,
      );
      this.setState({ showSwitchVersionModal: false });
    };

    if (type === AppVersionType.TESTING) {
      targetSwitch = _switchTestingVersion;
    } else {
      targetSwitch = _switchStableVersion;
    }

    const title = "Are you sure?";
    const desc = this._renderAppVersions();
    const buttonLabel = "Switch";

    return (
      <DialogModal
        title={title}
        desc={desc}
        onHide={_hide}
        onSuccess={targetSwitch}
        buttonLabel={buttonLabel}
      />
    );
  }

  _renderMakeStableModal() {
    if (!this.state.showMakeStableModal) {
      return null;
    }

    const _hide = () => {
      this.setState({ showMakeStableModal: false });
    };

    const _makeStable = () => {
      this._edit("stable", true);
      this.setState({ showMakeStableModal: false });
    };

    const title = "Are you sure?";
    const desc = `This operation is irreversible. All of the users \
      depending on your application will start using this version. Please \
      make sure you test your application extensively before marking it as \
      stable.`;
    const buttonLabel = "Mark As Stable";

    return (
      <DialogModal
        title={title}
        desc={desc}
        onHide={_hide}
        onSuccess={_makeStable}
        buttonLabel={buttonLabel}
      />
    );
  }

  _renderMakePrivateModal() {
    if (!this.state.showMakePrivateModal) {
      return null;
    }

    const _hide = () => {
      this.setState({ showMakePrivateModal: false });
    };

    const _makePrivate = () => {
      this._edit("public", false);
      this.setState({ showMakePrivateModal: false });
    };

    const title = "Are you sure?";
    const desc = `This operation will remove your application from the app \
      store Cortex users will not be able to use your application any more.`;
    const buttonLabel = "Make Private";

    return (
      <DialogModal
        title={title}
        desc={desc}
        onHide={_hide}
        onSuccess={_makePrivate}
        buttonLabel={buttonLabel}
      />
    );
  }

  _renderMakePublicModal() {
    if (!this.state.showMakePublicModal) {
      return null;
    }

    const _hide = () => {
      this.setState({ showMakePublicModal: false });
    };

    const _makePublic = () => {
      this._edit("public", true);
      this.setState({ showMakePublicModal: false });
    };

    const title = "Are you sure?";
    const desc = `This operation will make your application available on the \
      app store. Any Cortex user will be able to use your application.`;
    const buttonLabel = "Make Public";

    return (
      <DialogModal
        title={title}
        desc={desc}
        onHide={_hide}
        onSuccess={_makePublic}
        buttonLabel={buttonLabel}
      />
    );
  }

  _renderAppDetails() {
    const { app, type } = this.props;
    const appVersion =
      type === AppVersionType.STABLE ? app.stable_version : app.testing_version;

    const appDownloadUrl = appVersion ? appVersion.download_url : "";
    const { author, homepage, email } = app;

    return (
      <div className="app-details-author">
        <dl className="dl-horizontal">
          <dt>Author</dt>
          <dd>{author}</dd>
          <dt>Homepage</dt>
          <dd>
            <a target="_blank" rel="noopener noreferrer" href={`${homepage}`}>
              {homepage}
            </a>
          </dd>
          <dt>Support</dt>
          <dd>{email}</dd>
          {this._renderAppDates()}
          <dt>Download</dt>
          <dd>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${appDownloadUrl}`}
            >
              <i className="fa fa-download" />
            </a>
          </dd>
        </dl>
      </div>
    );
  }

  _renderAppDates() {
    const { app, type } = this.props;
    const appVersion = app[type];
    let dates = [
      <dt key="ud-dt">Upload Date</dt>,
      <dd key="ud-dd">{utcToLocal(appVersion.upload_date)}</dd>,
    ];

    if (type === AppVersionType.STABLE) {
      dates.push(<dt key="ss-dt">Stable Since</dt>);
      dates.push(<dd key="ss-dd">{utcToLocal(appVersion.change_date)}</dd>);
    }

    return dates;
  }

  _renderView() {
    const { app, storesLoaded } = this.props;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    if (!app) {
      return <Redirect to="/404" />;
    }

    const appIcon = icon(app);

    return (
      <div>
        <Box title="Details" toolbox={this._badges()}>
          <Row>
            <Col xs={2}>
              <img width="100%" src={appIcon} />
              {this._renderControls()}
            </Col>
            <Col xs={10}>
              <div className="app-details-desc">{app.description}</div>
              {this._renderAppDetails()}
            </Col>
          </Row>
        </Box>
        <Row>
          <Col xs={6}>{this._renderParameters()}</Col>
          <Col xs={6}>{this._renderScreenshots()}</Col>
        </Row>
      </div>
    );
  }

  render() {
    const { app, storesLoaded } = this.props;

    let title = null;
    let desc = null;
    let toolbox = null;

    if (storesLoaded && app) {
      title = this._title();
      desc = this._desc();
      toolbox = this._renderVersionSwitchButton();
    }
    const view = this._renderView();

    return (
      <Page title={title} desc={desc} toolbox={toolbox}>
        {view}
      </Page>
    );
  }
}

export { AppDetailsPage };
